export type TaskStatus =
  | 'active'
  | 'completed'
  | 'waiting'
  | 'idle'
  | 'queued'
  | 'assigned'
  | 'collecting'
  | 'collect_uploading'
  | 'collect_pending'
  | 'collect_completed'
  | 'collect_failed'
  | 'collect_cancelled'
  | 'collect_expired'
  | 'pending_generate'
  | 'generating'
  | 'ready'
  | 'uploaded'
  | 'generation_failed'

export type TaskAction = 'workspace' | 'retry' | 'pending' | 'generate'

export interface TaskActionMeta {
  action: TaskAction
  labelKey: string
  disabled: boolean
  showSpinner: boolean
}

export interface TaskPreviewMeta {
  enabled: boolean
  tooltipKey: string
}

export interface TaskErrorMeta {
  show: boolean
  messageKey: string
  message: string | null
}

export const FAILED_STATUSES: TaskStatus[] = [
  'collect_failed',
  'collect_cancelled',
  'collect_expired',
  'generation_failed'
]

export const TASK_ERROR_SUMMARY_MAX_LENGTH = 40

export const TASK_TOOLTIP_CLASS =
  'absolute z-[70] bottom-full left-1/2 -translate-x-1/2 mb-2 max-w-[280px] whitespace-normal break-words text-center bg-gray-800 text-white text-[10px] py-2 px-3 rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200 pointer-events-none'

interface TaskStatusStyle {
  pill: string
  dot: string
  progress: string
  percent: string
}

const STATUS_STYLES: Record<TaskStatus, TaskStatusStyle> = {
  active: {
    pill: 'bg-blue-50 text-blue-600',
    dot: 'bg-blue-600',
    progress: 'bg-blue-500',
    percent: 'text-blue-500'
  },
  completed: {
    pill: 'bg-green-50 text-green-600',
    dot: 'bg-green-600',
    progress: 'bg-green-500',
    percent: 'text-green-500'
  },
  waiting: {
    pill: 'bg-orange-50 text-orange-500',
    dot: 'bg-orange-500',
    progress: 'bg-gray-300',
    percent: 'text-gray-400'
  },
  idle: {
    pill: 'bg-gray-100 text-gray-500',
    dot: 'bg-gray-400',
    progress: 'bg-gray-300',
    percent: 'text-gray-400'
  },
  queued: {
    pill: 'bg-orange-50 text-orange-500',
    dot: 'bg-orange-500',
    progress: 'bg-gray-300',
    percent: 'text-gray-400'
  },
  assigned: {
    pill: 'bg-blue-50 text-blue-600',
    dot: 'bg-blue-600',
    progress: 'bg-blue-500',
    percent: 'text-blue-500'
  },
  collecting: {
    pill: 'bg-blue-50 text-blue-600',
    dot: 'bg-blue-600',
    progress: 'bg-blue-500',
    percent: 'text-blue-500'
  },
  collect_uploading: {
    pill: 'bg-blue-50 text-blue-600',
    dot: 'bg-blue-600',
    progress: 'bg-blue-500',
    percent: 'text-blue-500'
  },
  collect_pending: {
    pill: 'bg-orange-50 text-orange-500',
    dot: 'bg-orange-500',
    progress: 'bg-gray-300',
    percent: 'text-gray-400'
  },
  collect_completed: {
    pill: 'bg-green-50 text-green-600',
    dot: 'bg-green-600',
    progress: 'bg-green-500',
    percent: 'text-green-500'
  },
  collect_failed: {
    pill: 'bg-red-50 text-red-600',
    dot: 'bg-red-600',
    progress: 'bg-red-500',
    percent: 'text-red-500'
  },
  collect_cancelled: {
    pill: 'bg-red-50 text-red-600',
    dot: 'bg-red-600',
    progress: 'bg-red-500',
    percent: 'text-red-500'
  },
  collect_expired: {
    pill: 'bg-red-50 text-red-600',
    dot: 'bg-red-600',
    progress: 'bg-red-500',
    percent: 'text-red-500'
  },
  pending_generate: {
    pill: 'bg-orange-50 text-orange-500',
    dot: 'bg-orange-500',
    progress: 'bg-gray-300',
    percent: 'text-gray-400'
  },
  generating: {
    pill: 'bg-blue-50 text-blue-600',
    dot: 'bg-blue-600',
    progress: 'bg-blue-500',
    percent: 'text-blue-500'
  },
  ready: {
    pill: 'bg-green-50 text-green-600',
    dot: 'bg-green-600',
    progress: 'bg-green-500',
    percent: 'text-green-500'
  },
  uploaded: {
    pill: 'bg-green-50 text-green-600',
    dot: 'bg-green-600',
    progress: 'bg-green-500',
    percent: 'text-green-500'
  },
  generation_failed: {
    pill: 'bg-red-50 text-red-600',
    dot: 'bg-red-600',
    progress: 'bg-red-500',
    percent: 'text-red-500'
  }
}

export const getTaskStatusStyle = (status: TaskStatus): TaskStatusStyle => STATUS_STYLES[status]

export const isTaskFailed = (status: TaskStatus) => FAILED_STATUSES.includes(status)

export const summarizeTaskError = (message?: string | null, maxLength: number = TASK_ERROR_SUMMARY_MAX_LENGTH) => {
  if (!message) {
    return ''
  }
  const cleaned = message.replace(/\s+/g, ' ').trim()
  if (!cleaned) {
    return ''
  }
  if (cleaned.length <= maxLength) {
    return cleaned
  }
  return `${cleaned.slice(0, Math.max(1, maxLength - 1))}…`
}

export const normalizeTaskProgress = (status: TaskStatus, percent: number) => {
  const safePercent = Math.min(100, Math.max(0, Number.isFinite(percent) ? percent : 0))
  if (isTaskFailed(status)) {
    return 0
  }
  return safePercent
}

export const getTaskActionForStatus = (status: TaskStatus): TaskAction => {
  if (['collect_completed', 'pending_generate'].includes(status)) {
    return 'generate'
  }
  if (['ready', 'uploaded', 'completed'].includes(status)) {
    return 'workspace'
  }
  if (['collect_failed', 'generation_failed', 'collect_cancelled', 'collect_expired'].includes(status)) {
    return 'retry'
  }
  return 'pending'
}

export const resolveTaskActionMeta = (status: TaskStatus): TaskActionMeta => {
  const action = getTaskActionForStatus(status)
  if (action === 'generate') {
    return { action, labelKey: 'taskCenter.actions.generate', disabled: false, showSpinner: false }
  }
  if (action === 'workspace') {
    return { action, labelKey: 'taskCenter.actions.workspace', disabled: false, showSpinner: false }
  }
  if (action === 'retry') {
    const labelKey =
      status === 'generation_failed' ? 'taskCenter.actions.retryGenerate' : 'taskCenter.actions.retryCollect'
    return { action, labelKey, disabled: false, showSpinner: false }
  }
  const showSpinner = ['assigned', 'collecting', 'collect_uploading', 'generating'].includes(status)
  return { action, labelKey: `taskCenter.status.${status}`, disabled: true, showSpinner }
}

export const resolveTaskPreviewMeta = (status: TaskStatus, hasCollectionId: boolean): TaskPreviewMeta => {
  if (!hasCollectionId) {
    return { enabled: false, tooltipKey: 'taskCenter.tooltips.previewUnavailable' }
  }

  const canPreviewStatuses: TaskStatus[] = [
    'collect_completed',
    'pending_generate',
    'generating',
    'ready',
    'uploaded',
    'generation_failed'
  ]
  if (canPreviewStatuses.includes(status)) {
    return { enabled: true, tooltipKey: 'taskCenter.actions.preview' }
  }

  if (['collect_failed', 'collect_cancelled', 'collect_expired'].includes(status)) {
    return { enabled: false, tooltipKey: 'taskCenter.tooltips.previewFailed' }
  }

  return { enabled: false, tooltipKey: 'taskCenter.tooltips.previewCollecting' }
}

export const resolveTaskErrorMeta = (status: TaskStatus, errorMessage?: string | null): TaskErrorMeta => {
  if (!isTaskFailed(status)) {
    return { show: false, messageKey: '', message: null }
  }
  if (errorMessage) {
    return { show: true, messageKey: '', message: errorMessage }
  }
  return { show: true, messageKey: 'taskCenter.tooltips.errorFallback', message: null }
}

export const formatTaskDate = (value?: string | null): string => {
  if (!value) {
    return '--'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '--'
  }
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  return `${month}-${day},${hour}:${minute}`
}
